import { useEffect, useRef, useState } from "react";
import { honeyStockApi } from "../../services/businessApi";
import { Field } from "./Field";
import { PromptDialog } from "./PromptDialog";
import { useToast } from "../../context/ToastContext";

const NEW_VARIETAL = "__new__";

/**
 * The account's own "General" row, or null.
 *
 * ONE RESOLVER, mirroring mobile's export of the same name -- the pair is
 * registered in platformParity.test.js. Forms outside this component need
 * it too (the vendor send form has its own select), and the alternative
 * is each of them carrying its own copy of a string comparison.
 *
 * Returns an ID, not null-meaning-General. Since V080 gave every account
 * a real General row, `null` here means NOTHING IS SELECTED.
 */
export function generalVarietalId(varietals) {
  const general = (varietals || []).find(
    (v) => v.is_active && v.name?.trim().toLowerCase() === "general",
  );
  return general ? general.varietal_id : null;
}

/**
 * Choose which honey this is -- or name a new one.
 *
 * Extracted while wiring the harvest form to the honey pool (V091). The
 * same dropdown, with the same "+ New varietal..." escape hatch, was
 * already written out four times inside HoneyPoolPage; the harvest dialog
 * would have been the fifth. Four copies is where a control stops being
 * a pattern and starts being a liability -- the "+ New" flow alone is a
 * sentinel value, a prompt, a create call, a re-list and a re-select, and
 * getting one of those wrong in one copy is invisible until somebody uses
 * that particular dialog.
 *
 * TIER, DELIBERATELY THE CALLER'S PROBLEM. This renders nothing when
 * `enabled` is false, and the caller decides what enables it -- but the
 * caller must still SEND a varietal, because V049's argument holds
 * everywhere: the picker is gated to Business, the column never is. A
 * Harvester account records honey against General and gets its varietals
 * back intact if it upgrades. Gating the write instead would make a
 * downgrade destructive.
 *
 * Loads its own list, as HivePicker does, because the alternative is
 * every form growing its own fetch and its own stale copy.
 */
export function VarietalPicker({
  label = "Varietal", hint, value, onChange, enabled = true,
  /*
   * What the "" option says when General is a real row you can pick.
   * Only read in that case -- see the option below.
   */
  placeholder = "Select a varietal…",
  /*
   * Land on General rather than on nothing.
   *
   * For the CAPTURE forms -- bottling, adding to the pool, recording a
   * harvest -- where the server already resolves an omitted varietal to
   * General. Showing it is strictly more honest than leaving the field
   * blank and filing it there anyway.
   *
   * NOT for "correct the pool", where the choice decides which balance
   * moves, or for unbottling, where it narrows the sizes. A default there
   * is a guess with consequences rather than a visible restatement of
   * what would happen regardless.
   */
  defaultGeneral = false,
  /*
   * "record" (honey that did not exist before) or "move" (honey that
   * already exists changing form or owner). Decides whether a put-away
   * varietal with a balance is still offered -- see below. Defaults to
   * the conservative answer, so a form that says nothing does not offer
   * one.
   */
  purpose = "record",
}) {
  const { notify } = useToast();
  const [varietals, setVarietals] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // The setter awaiting a name, or null (#328): a PromptDialog rather
  // than window.prompt, which the user can switch off -- it then returns
  // null silently and naming a varietal simply stops working.
  const [naming, setNaming] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // No request when nothing will be rendered. A Free account asking for
    // a varietal list is a call whose result is never read.
    if (!enabled) return undefined;
    let cancelled = false;
    honeyStockApi
      .listVarietals()
      .then((r) => !cancelled && setVarietals(r.varietals || []))
      .catch(() => !cancelled && setVarietals([]))
      .finally(() => !cancelled && setLoaded(true));
    return () => { cancelled = true; };
  }, [enabled]);

  /*
   * WHICH PUT-AWAY VARIETALS ARE STILL OFFERED, AND WHERE (#358).
   *
   * Reported from a device: a varietal put away because it still had
   * honey "stayed available when I went to add new honey". Right, and it
   * contradicted the rule this comment used to state -- one filter was
   * applied to every form, so "stop offering this for NEW honey" was not
   * what the code did.
   *
   *   purpose="record"  Honey that did not exist before -- a harvest,
   *                     adding to the pool. A put-away varietal is not
   *                     offered. The default.
   *
   *   purpose="move"    Honey that already exists changing form or owner
   *                     -- bottling, emptying jars back, correcting a
   *                     balance, sending to a vendor. A put-away varietal
   *                     IS offered while it has a balance, because jars
   *                     whose varietal cannot be chosen cannot be
   *                     bottled, sold or moved.
   *
   * So it disappears in TWO STAGES: at once from everything that records
   * new honey, and from the rest when its balance reaches zero.
   *
   * MUST MATCH mobile's varietalOptions, which is why both are registered
   * as a pair in platformParity.
   */
  const stillStocked = (v) => purpose === "move" && Number(v.pounds_on_hand) > 0;
  const active = varietals.filter((v) => v.is_active || stillStocked(v));
  // If the account has a varietal actually NAMED General, do not offer a
  // second, blank one beside it -- two rows reading "General" is a user
  // wondering which is which.
  const hasRealGeneral = active.some((v) => v.name?.trim().toLowerCase() === "general");

  /*
   * THE SELECT MUST NEVER DISPLAY AN OPTION IT IS NOT HOLDING.
   *
   * It did. `value` is "" until somebody chooses, and the "" option was
   * only rendered when the account had no varietal NAMED General -- which,
   * since V080 gave every account one, is no account at all. A controlled
   * select whose value matches no option falls back to rendering the
   * FIRST one, so the form showed "Clover" and submitted undefined, which
   * the server files under General.
   *
   * Displayed one varietal, recorded another, and plausibly enough that
   * nobody would notice. Same shape as the onPressIn/onPress bug on
   * mobile: what is shown and what is sent disagreeing.
   *
   * So there is always exactly one option for "", and what it SAYS
   * depends on what "" means here: with no real General it genuinely is
   * General (the server resolves it), and with one it means "not chosen".
   */
  const generalId = generalVarietalId(active);

  /* Fired once, and only once the list has actually arrived -- defaulting
     against an empty list would select nothing and then never retry.
     onChange is deliberately out of the deps: callers pass inline arrows,
     so depending on it re-runs this on every render. */
  const defaulted = useRef(false);
  useEffect(() => {
    if (!defaultGeneral || defaulted.current) return;
    if (!loaded || value || generalId == null) return;
    defaulted.current = true;
    onChange(String(generalId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultGeneral, loaded, value, generalId]);

  async function create(name) {
    if (!name) return setNaming(false);
    try {
      const created = await honeyStockApi.createVarietal(name);
      const r = await honeyStockApi.listVarietals();
      setVarietals(r.varietals || []);
      // Selected, not merely created: otherwise the user names a varietal
      // mid-bottling and the dropdown snaps back to General, and they
      // have to find it again in a list they just added to.
      onChange(String(created.varietal_id));
      setNaming(false);
      // `created` distinguishes a new one from a name the account already
      // had -- the server is idempotent on name, so "Added Clover" would
      // be a small lie the second time.
      notify(created.created ? `Added ${created.name}` : `${created.name} was already on your list`, "success");
    } catch (err) {
      setError(err.apiMessage || "Could not add that varietal");
      setNaming(false);
    }
  }

  if (!enabled || (loaded && active.length === 0)) return null;

  return (
    <>
      <Field label={label} hint={error || hint}>
        {(id, aria) => (
          <select
            id={id}
            {...aria}
            className="touch-target"
            value={value ?? ""}
            onChange={(e) => {
              if (e.target.value === NEW_VARIETAL) {
                setNaming(true);
                return;
              }
              onChange(e.target.value);
            }}
          >
            <option value="">{hasRealGeneral ? placeholder : "General"}</option>
            {active.map((v) => (
              <option key={v.varietal_id} value={v.varietal_id}>{v.name}</option>
            ))}
            <option value={NEW_VARIETAL}>+ New varietal…</option>
          </select>
        )}
      </Field>
      <PromptDialog
        open={naming}
        onClose={() => setNaming(false)}
        onSubmit={create}
        title="Add a honey"
        label="What do you want to call this honey?"
        hint="Clover, Wildflower, Spring Blend — whatever you call it on the jar."
        confirmLabel="Add"
        // 50, matching the server. A prompt that accepts more than the
        // API does refuses the name only after the user presses Add.
        maxLength={50}
      />
    </>
  );
}
